import { type RefObject, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import type { ItemEmExecucao, Nota } from "@noesis/shared";
import { FalhaDaApi, pedir } from "../api.js";
import { Icone } from "../componentes/Icone.js";
import { IconeCriar, IconeFechar } from "../componentes/icones.js";
import { EditorRico } from "./EditorRico.js";

/**
 * O caderno aberto **durante** a sessão de estudo — RN-42.
 *
 * Ele existe porque a nota nasce no momento em que a dúvida aparece. Guardar
 * "escrever sobre isso depois" na cabeça até o fim da sessão é a forma mais
 * confiável de não escrever.
 *
 * **A página é da sessão, não do item** (RN-43, revista). Na primeira vez que o
 * caderno abre numa sessão de estudo, a página é **em branco**: ela só passa a
 * existir no banco quando há o que guardar, na pasta do item e com o item
 * anexado. Daí em diante a página da sessão fica a mesma ao trocar de questão
 * e ao fechar e reabrir o painel (quem guarda o id é a tela, `Executar`). O
 * seletor do topo troca para as outras páginas da matéria, e "Página" abre
 * outra em branco. Antes, a página acompanhava o assunto do item e era sempre
 * a mesma — quem queria só anotar algo novo não tinha como.
 *
 * Antes de qualquer troca de página a gravação pendente é descarregada — a
 * RN-44 não pode perder frase numa troca.
 *
 * **Duas formas por largura** (RN-30/31), e a regra nomeia a armadilha: a
 * partir de `--quebra-caderno` é coluna em fluxo, fixada por `position:
 * sticky`, repartindo a tela com o enunciado; abaixo dela é folha ancorada com
 * altura medida. `position: fixed` cobrindo o enunciado é defeito conhecido,
 * não alternativa — quem está respondendo precisa continuar lendo a questão
 * enquanto escreve sobre ela.
 *
 * **Fica desligado no simulado** (RN-55). Quem monta a tela decide isso; aqui
 * não há verificação de modo, porque um componente que se recusa a aparecer é
 * mais difícil de entender do que um que simplesmente não é montado.
 *
 * **A escrita aqui é uma só, e é a página do assunto** (RN-42, ADR 020). Houve
 * aqui dentro uma segunda gaveta de texto, a anotação presa ao item, e ela saiu
 * em 2026-09-13: eram duas escritas sobre a mesma coisa, e a do item duplicava
 * a página do assunto (RN-43) contra o "um lugar só" do §1. O caderno é o lugar
 * de escrever — conteúdo, dúvida e resumo.
 *
 * **E o assistente também não mora aqui** (ADR 028). O campo de perguntar viveu
 * um tempo no fim deste painel, e ali ele era apêndice de uma tela de escrita:
 * este painel é onde se **escreve**, o do assistente é onde se **pergunta**, e a
 * resposta do modelo só entra numa página por um toque explícito (RN-62) — que é
 * exatamente a fronteira que some quando os dois dividem a mesma superfície.
 *
 * A gravação é a mesma do editor do caderno: debounce com flush ao fechar e ao
 * desmontar (RN-44). Fechar o painel no meio de uma frase não pode perdê-la.
 */

type Contexto =
  | { nome: "carregando" }
  | {
      nome: "pronto";
      materia: string | null;
      /** A pasta do item, pelo id: é nela que uma página nova nasce. */
      categoriaIdDoItem: string;
      irmas: { id: string; titulo: string; categoria: string[] }[];
    }
  | { nome: "erro"; mensagem: string };

/** Só há o que gravar se sobrou texto (ou imagem) depois de tirar as marcas. */
function temConteudo(html: string): boolean {
  return html.replace(/<(?!img\b)[^>]*>/g, "").replace(/&nbsp;/g, " ").trim() !== "" || /<img\b/.test(html);
}

export function PainelDoCaderno({
  item,
  paginaId,
  aoMudarPagina,
  aoFechar,
}: {
  item: ItemEmExecucao;
  /** A página da sessão: `null` é a em branco, que ainda não existe no banco. */
  paginaId: string | null;
  aoMudarPagina: (id: string | null) => void;
  aoFechar: () => void;
}) {
  const itemId = item.id;
  const [contexto, setContexto] = useState<Contexto>({ nome: "carregando" });
  const [nota, setNota] = useState<Nota | null>(null);
  const [carregandoPagina, setCarregandoPagina] = useState(paginaId !== null);
  const [html, setHtml] = useState("");
  const [gravando, setGravando] = useState(false);
  const [erroDePagina, setErroDePagina] = useState<string | null>(null);

  const raiz = useRef<HTMLElement>(null);
  const botaoFechar = useRef<HTMLButtonElement>(null);
  const atual = useRef({ id: null as string | null, html: "", titulo: "", categoriaId: null as string | null, anexados: [] as string[], base: "" });
  const sujo = useRef(false);
  const criandoRef = useRef(false);
  const gravarRef = useRef<() => Promise<void>>(async () => {});
  const destino = useRef<{ categoriaId: string | null; itemId: string }>({ categoriaId: null, itemId });
  destino.current = {
    categoriaId: contexto.nome === "pronto" ? contexto.categoriaIdDoItem : null,
    itemId,
  };

  /*
   * O foco entra no painel ao abrir, e para no "Fechar o caderno".
   *
   * Quem abriu estava com o foco no alvo do rodapé, e o menu inteiro deixa de
   * ser montado com o painel na tela (RN-42): sem isto o foco cairia no `body` e a
   * próxima tabulação recomeçaria do topo do documento. Só na montagem — a
   * troca de item recarrega o contexto sem remontar o painel, e ali roubar o
   * foco tiraria a mão de quem já voltou a ler a questão.
   */
  useEffect(() => {
    botaoFechar.current?.focus();
  }, []);

  const adotar = useCallback((n: Nota | null) => {
    setNota(n);
    setHtml(n?.html ?? "");
    atual.current = {
      id: n?.id ?? null,
      html: n?.html ?? "",
      titulo: n?.titulo ?? "",
      categoriaId: n?.categoriaId ?? null,
      anexados: n?.itensAnexados ?? [],
      base: n?.atualizadoEm ?? "",
    };
    sujo.current = false;
  }, []);

  // O contexto acompanha o item (matéria e pasta mudam com ele); a página, não.
  useEffect(() => {
    let vivo = true;
    void pedir("nota.contextoDoItem", { params: { id: itemId } })
      .then((r) => {
        if (!vivo) return;
        setContexto({ nome: "pronto", materia: r.materia, categoriaIdDoItem: r.categoriaIdDoItem, irmas: r.irmas });
      })
      .catch((e: unknown) => {
        if (!vivo) return;
        setContexto({
          nome: "erro",
          mensagem: e instanceof FalhaDaApi ? e.message : "Não foi possível abrir o caderno.",
        });
      });
    return () => {
      vivo = false;
    };
  }, [itemId]);

  /*
   * A página da sessão mudou — na montagem, pelo seletor ou pelo "Página".
   *
   * A que acabou de ser criada aqui já é a aberta (`atual.id` bate com
   * `paginaId`) e não se recarrega: reler a página no meio da digitação
   * passaria por cima do que foi escrito depois do pedido. A gravação pendente
   * da página anterior é descarregada antes da troca (RN-44).
   */
  useEffect(() => {
    if (paginaId === atual.current.id) {
      setCarregandoPagina(false);
      return;
    }
    let vivo = true;
    void gravarRef.current();
    setErroDePagina(null);
    if (paginaId === null) {
      adotar(null);
      setCarregandoPagina(false);
      return;
    }
    setCarregandoPagina(true);
    void pedir("nota.ver", { params: { id: paginaId } })
      .then((r) => {
        if (!vivo) return;
        adotar(r.nota);
        setCarregandoPagina(false);
      })
      .catch((e: unknown) => {
        if (!vivo) return;
        setErroDePagina(e instanceof FalhaDaApi ? e.message : "Não foi possível abrir a página.");
        setCarregandoPagina(false);
      });
    return () => {
      vivo = false;
    };
  }, [paginaId, adotar]);

  /*
   * Grava a página aberta. A em branco **não existe** até haver o que guardar:
   * a primeira gravação com texto é quem a cria, na pasta do item, com o item
   * anexado — o registro de onde a nota nasceu (§4). Abrir o caderno e fechar
   * sem escrever não deixa página vazia para trás.
   */
  const gravar = useCallback(async () => {
    const v = atual.current;
    if (!sujo.current) return;

    if (!v.id) {
      const { categoriaId, itemId: anexo } = destino.current;
      if (criandoRef.current || !categoriaId || !temConteudo(v.html)) return;
      criandoRef.current = true;
      sujo.current = false;
      setGravando(true);
      const enviado = v.html;
      try {
        const dia = new Date().toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
        const r = await pedir("nota.criar", {
          corpo: { titulo: `Anotações · ${dia}`, html: enviado, categoriaId, itensAnexados: [anexo] },
        });
        v.id = r.nota.id;
        v.titulo = r.nota.titulo;
        v.categoriaId = r.nota.categoriaId;
        v.anexados = r.nota.itensAnexados;
        v.base = r.nota.atualizadoEm;
        setNota(r.nota);
        setContexto((c) =>
          c.nome === "pronto"
            ? { ...c, irmas: [{ id: r.nota.id, titulo: r.nota.titulo, categoria: r.nota.categoria }, ...c.irmas] }
            : c,
        );
        aoMudarPagina(r.nota.id);
        // O que foi digitado enquanto o pedido ia ainda precisa subir.
        if (v.html !== enviado) sujo.current = true;
      } catch {
        sujo.current = true;
      } finally {
        criandoRef.current = false;
        setGravando(false);
      }
      if (sujo.current) void gravarRef.current();
      return;
    }

    sujo.current = false;
    setGravando(true);
    try {
      const r = await pedir("nota.salvar", {
        params: { id: v.id },
        corpo: {
          titulo: v.titulo,
          html: v.html,
          categoriaId: v.categoriaId,
          itensAnexados: v.anexados,
          baseAtualizadoEm: v.base,
        },
      });
      if (r.resultado === "salvo") {
        v.base = r.nota.atualizadoEm;
      } else {
        /*
         * Conflito aqui não abre a tela de duas versões: o painel é uma janela
         * de escrita rápida no meio de uma sessão, e parar a sessão para
         * resolver conflito é o oposto do que ele existe para fazer. A versão
         * do servidor entra como base e a próxima gravação vai por cima — que é
         * a decisão que o caderno inteiro oferece como "manter a minha", só que
         * assumida aqui.
         */
        v.base = r.versaoDoServidor.atualizadoEm;
        if (atual.current === v) sujo.current = true;
      }
    } catch {
      if (atual.current === v) sujo.current = true;
    } finally {
      setGravando(false);
    }
  }, [aoMudarPagina]);

  gravarRef.current = gravar;

  /** Troca de página: descarrega a atual e só então muda o id da sessão. */
  function irPara(id: string | null) {
    void gravarRef.current();
    aoMudarPagina(id);
  }

  // Debounce mais os flushes, iguais aos do editor do caderno (RN-44).
  useEffect(() => {
    if (!sujo.current) return;
    const t = setTimeout(() => void gravarRef.current(), 800);
    return () => clearTimeout(t);
  }, [html]);

  useEffect(() => {
    const aoEsconder = () => {
      if (document.visibilityState === "hidden") void gravarRef.current();
    };
    document.addEventListener("visibilitychange", aoEsconder);
    return () => {
      document.removeEventListener("visibilitychange", aoEsconder);
      void gravarRef.current();
    };
  }, []);

  function fechar() {
    void gravarRef.current();
    aoFechar();
  }

  /*
   * A folha do estreito nasce no fim do fluxo, e a regra diz que "a página rola
   * até ele". Ela não rolava: o painel abria abaixo da dobra, e a barra de
   * ferramentas do editor — que é a parte de cima dele — ficava atrás da barra
   * de ação e da navegação inferior. Limitar a altura resolve metade do
   * problema; a outra metade é chegar lá.
   *
   * No largo o painel é `sticky` ao lado do enunciado e já está à vista: rolar
   * ali moveria a questão que a pessoa está lendo. A faixa é `--quebra-caderno`
   * e não `--quebra-largo`: entre as duas o painel voltou a ser folha no fim do
   * fluxo, porque duas colunas não cabem ali (ADR 022).
   */
  useEffect(() => {
    if (window.matchMedia("(min-width: 80rem)").matches) return;
    // Depois de **pronto**, não ao montar: montado o painel ainda é o
    // esqueleto de carregamento, e rolar até ele nessa altura mira uma caixa
    // pequena que cresce logo em seguida — a barra de ferramentas acaba
    // nascendo abaixo da dobra do mesmo jeito.
    if (contexto.nome !== "pronto") return;
    raiz.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [contexto.nome]);

  const materiaAberta = contexto.nome === "pronto" ? contexto.materia : null;
  const titulo = nota?.titulo ?? "Página nova";
  const pronto = contexto.nome === "pronto" && !carregandoPagina;

  useEffect(() => alinharPauta(raiz.current), [pronto]);

  return (
    <aside className="painel-sessao painel-caderno" aria-label="Caderno da matéria" ref={raiz}>
      <AlcaDeLargura painel={raiz} />
      <div className="painel-caderno__cabecalho">
        <div className="painel-caderno__identificacao">
          <p className="etiqueta">Caderno</p>
          <p className="painel-caderno__materia">{materiaAberta ?? "Sem matéria"}</p>
        </div>
        <div className="linha painel-caderno__acoes">
          <button
            type="button"
            className="botao botao--secundario botao--compacto"
            disabled={contexto.nome !== "pronto" || paginaId === null}
            title="Abrir uma página em branco"
            onClick={() => irPara(null)}
          >
            <Icone glifo={IconeCriar} papel="acao" tamanho="p" />
            Página
          </button>
          <button
            type="button"
            ref={botaoFechar}
            className="lateral__alternar"
            aria-label="Fechar o caderno"
            title="Fechar o caderno · Esc"
            onClick={fechar}
          >
            <Icone glifo={IconeFechar} papel="acao" />
          </button>
        </div>
      </div>

      {/*
        A área que rola é **esta**, e o cabeçalho está fora dela. Antes rolava o
        painel inteiro e o cabeçalho se segurava com `position: sticky` mais
        fundo e `z-index`: o texto passava por trás dele e dava para ler as duas
        camadas juntas. Delimitar a caixa que rola tira o problema — o que rola
        não passa por baixo do que não rola.
      */}
      <div className="painel-sessao__corpo">
        {(contexto.nome === "carregando" || carregandoPagina) && (
          <div aria-busy="true">
            <span className="so-leitor" role="status">
              Abrindo o caderno
            </span>
            <div className="esqueleto esqueleto--bloco" />
          </div>
        )}

        {contexto.nome === "erro" && (
          <p className="campo__erro" role="alert">
            {contexto.mensagem}
          </p>
        )}

        {erroDePagina && (
          <p className="campo__erro" role="alert">
            {erroDePagina}
          </p>
        )}

        {pronto && (
          <>
            <div className="painel-caderno__pagina">
              <label className="so-leitor" htmlFor="caderno-pagina">
                Página do caderno
              </label>
              <select
                id="caderno-pagina"
                className="campo__entrada painel-caderno__seletor"
                value={paginaId ?? ""}
                onChange={(e) => irPara(e.target.value === "" ? null : e.target.value)}
              >
                {paginaId === null && <option value="">Página em branco</option>}
                {nota && !contexto.irmas.some((p) => p.id === nota.id) && (
                  <option value={nota.id}>{nota.titulo}</option>
                )}
                {contexto.irmas.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.titulo}
                  </option>
                ))}
              </select>
              {gravando && <span className="tinta-suave"> · salvando…</span>}
            </div>

            <EditorRico
              compacta
              html={html}
              rotulo={`Caderno de ${titulo}`}
              aoSalvar={() => {
                sujo.current = true;
                void gravarRef.current();
              }}
              aoMudar={(novo) => {
                sujo.current = true;
                atual.current.html = novo;
                setHtml(novo);
              }}
            />
          </>
        )}
      </div>

      {nota && (
        <Link className="botao--texto painel-caderno__abrir" to={`/caderno/${nota.id}`}>
          Abrir no caderno
        </Link>
      )}
    </aside>
  );
}

/**
 * Põe a pauta do corpo e a primeira linha do editor na fase da pauta do painel.
 * O que fica acima deles (cabeçalho, seletor, barra) tem altura que não é
 * múltiplo da pauta, e a barra muda de altura ao quebrar com a largura: por
 * isso é medida, e medida de novo a cada mudança de tamanho.
 */
function alinharPauta(painel: HTMLElement | null) {
  if (!painel) return;
  const alinhar = () => {
    const corpo = painel.querySelector<HTMLElement>(".painel-sessao__corpo");
    if (!corpo) return;
    const pauta = parseFloat(getComputedStyle(painel).getPropertyValue("--pauta"));
    const topoCorpo = corpo.getBoundingClientRect().top - painel.getBoundingClientRect().top;
    painel.style.setProperty("--fase-corpo", `${-(topoCorpo % pauta)}px`);
    const superficie = corpo.querySelector<HTMLElement>(".editor-rico__superficie");
    if (!superficie) return;
    const topo = topoCorpo + superficie.getBoundingClientRect().top - corpo.getBoundingClientRect().top + corpo.scrollTop;
    painel.style.setProperty("--fase-editor", `${(pauta - (topo % pauta)) % pauta}px`);
  };
  const observador = new ResizeObserver(alinhar);
  observador.observe(painel);
  painel.querySelectorAll(".painel-sessao__corpo > *, .editor-rico__barra").forEach((el) => observador.observe(el));
  return () => observador.disconnect();
}

const CHAVE_LARGURA = "noesis:painel-caderno";
const PASSO_DO_TECLADO = 32;

function pedirLargura(px: number) {
  // CSSOM, não atributo `style`: a CSP não barra. O limite é o clamp do token.
  document.documentElement.style.setProperty("--painel-caderno-pedido", `${Math.round(px)}px`);
}

// A largura escolhida é preferência do aparelho, como a barra lateral.
try {
  const guardada = Number(localStorage.getItem(CHAVE_LARGURA));
  if (guardada > 0) pedirLargura(guardada);
} catch {
  /* sem armazenamento, fica o padrão */
}

/**
 * A borda esquerda do painel, arrastável. Só grava a largura pedida; quem
 * segura entre o mínimo e 40% da tela é o `clamp` de `--painel-caderno`.
 */
function AlcaDeLargura({ painel }: { painel: RefObject<HTMLElement | null> }) {
  const [arrastando, setArrastando] = useState(false);
  const [largura, setLargura] = useState<number>();

  function aplicar(px: number) {
    pedirLargura(px);
    // Lê de volta o que o clamp deixou, para o leitor de tela e o armazenamento.
    const real = painel.current?.getBoundingClientRect().width ?? px;
    setLargura(Math.round(real));
    try {
      localStorage.setItem(CHAVE_LARGURA, String(Math.round(real)));
    } catch {
      /* segue sem lembrar */
    }
  }

  return (
    <div
      className="painel-caderno__alca"
      role="separator"
      aria-orientation="vertical"
      aria-label="Largura do caderno"
      aria-valuenow={largura}
      tabIndex={0}
      data-arrastando={arrastando || undefined}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        setArrastando(true);
      }}
      onPointerMove={(e) => {
        if (arrastando) aplicar(window.innerWidth - e.clientX);
      }}
      onPointerUp={() => setArrastando(false)}
      onPointerCancel={() => setArrastando(false)}
      onKeyDown={(e) => {
        const atual = painel.current?.getBoundingClientRect().width ?? 0;
        if (e.key === "ArrowLeft") aplicar(atual + PASSO_DO_TECLADO);
        else if (e.key === "ArrowRight") aplicar(atual - PASSO_DO_TECLADO);
        else return;
        e.preventDefault();
        e.stopPropagation();
      }}
    />
  );
}
